import { QuestionExpressionModel } from '@psyomics/surveyjs-core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../../components/atoms/dialog/dialog';
import { Header } from '../../../../../components/atoms/header/header';
import { Link } from '../../../../../components/atoms/link/link';
import { Text } from '../../../../../components/atoms/text/text';
import { cn } from '../../../../../shadcn/lib/utils';
import { useRenderContext } from '../../../context/render-context';
import { Survey } from '../../../survey-types';
import * as createBlockUtils from '../../../utils/create-block';
import { ResolveFields } from '../../../utils/create-block';
import { ExpressionTypes, getCalculationId, getExpressionRenderedText } from '../../../utils/surveys/expressions';
import { createExpressionField } from '../../../utils/surveys/fields';

type SubscalesProps = {
  readonly text?: string;
  readonly totalExpressionName?: string;
  readonly measureName?: string;
  readonly inline?: boolean;
  readonly isPdf?: boolean;
};

const ExpressionCalculationContent = ({
  renderedText,
  isPdf,
}: {
  readonly renderedText: string;
  readonly isPdf?: boolean;
}) => (
  <div
    className={cn('prose prose-hr:my-4', { 'overflow-y-auto max-h-[60vh]': !isPdf })}
    dangerouslySetInnerHTML={{ __html: renderedText }}
  />
);

const ShowCalculationDialog = ({
  conditionName,
  isOpen,
  setIsOpen,
  measureName,
  renderedText,
  t,
}: {
  readonly isOpen: boolean;
  readonly setIsOpen: (open: boolean) => void;
  readonly measureName?: string;
  readonly conditionName: string;
  readonly renderedText: string;
  readonly t: ReturnType<typeof useTranslation>['t'];
}) => {
  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogTrigger>
        <Link className="underline font-bold text-lg">{t('report.blocks.showCalculation.showCalculation')}</Link>
      </DialogTrigger>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[650px]">
        <DialogHeader>
          <DialogTitle asChild noStyle>
            <Header level="h3">{`${measureName ?? conditionName} ${t(
              'ui-components.showCalculation.calculation'
            )}`}</Header>
          </DialogTitle>
        </DialogHeader>
        <ExpressionCalculationContent renderedText={renderedText} />
      </DialogContent>
    </Dialog>
  );
};

const ShowCalculation = ({
  text,
  totalExpressionName,
  measureName,
  inline = false,
  isPdf = false,
  survey: propSurvey,
}: SubscalesProps & {
  readonly survey?: Survey;
}) => {
  const renderContext = useRenderContext();
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  const survey = propSurvey || renderContext.survey;

  if (!survey) return <div>No survey</div>;
  if (!totalExpressionName) return <div>No conditionExpression selected</div>;

  const conditionExpression = survey.model.getQuestionByName(totalExpressionName);

  if (!(conditionExpression instanceof QuestionExpressionModel)) {
    throw new TypeError('conditionExpression ' + conditionExpression?.name + ' not instance of question expression');
  }

  let content;

  if (isPdf) {
    content = (
      <Link
        className="underline font-bold"
        href={`#calculation-${getCalculationId(conditionExpression, survey.respondentType)}`}
      >
        {t('report.blocks.showCalculation.showCalculation')}
      </Link>
    );
  } else {
    const renderedText = getExpressionRenderedText(survey, conditionExpression);
    content = (
      <ShowCalculationDialog
        conditionName={conditionExpression.conditionName}
        isOpen={isOpen}
        measureName={measureName}
        renderedText={renderedText}
        setIsOpen={setIsOpen}
        t={t}
      />
    );
  }

  if (inline) {
    return content;
  }

  return (
    <Text weight="semibold">
      {text} {content}
    </Text>
  );
};

const resolveFields: ResolveFields<SubscalesProps> = ({ props }, { fields }) => {
  return {
    ...fields,
    ...(props.survey && {
      text: {
        type: 'text',
        label: 'Prefixed Text',
      },
      total: {
        type: 'text',
        label: 'Total calculation title',
      },
      totalExpressionName: {
        ...createExpressionField(props.survey, ExpressionTypes.MEASURE, props.page),
        label: 'Select total expression',
      },
    }),
  };
};

const createShowCalculationBlock = createBlockUtils.makeBlockFactory<SubscalesProps>(
  { withExampleAnswersJson: true, withPageSelect: true },
  {
    label: 'Show Calculations',
    render: ShowCalculation,
    resolveFields,
  }
);

export {
  createShowCalculationBlock,
  ExpressionCalculationContent,
  resolveFields,
  ShowCalculation,
  type SubscalesProps,
};
